import type { LanguageModelV4Message, LanguageModelV4ToolResultPart } from '@ai-sdk/provider';
import type { LanguageModelMiddleware } from 'ai';
import { generateId } from 'ai';

import { extractLeakedToolCalls, formatFunctionCallsBlock, formatFunctionResultsBlock } from '@sites/ai-connector';

/**
 * Bidirectional adapter between the AI SDK's native tool-calling protocol and the text-emulated
 * one this backend proxy actually speaks for Claude models. Verified live, both directions:
 *
 * - INBOUND: the gateway never returns structured `tool_calls` (the request carries a correct
 *   `tools` array, yet the response is `finish_reason: "stop"` with the calls written as
 *   pseudo-XML `<function_calls>` blocks in plain `message.content`).
 * - OUTBOUND: the gateway forwards messages to Anthropic's Messages API verbatim, which rejects
 *   OpenAI-format tool messages outright (`Unexpected role "tool". Allowed roles are "user" or
 *   "assistant"`), so native tool-call/tool-result messages must never reach the wire.
 *
 * `wrapGenerate` rewrites leaked XML in the response into REAL tool-call content parts, and
 * `transformParams` rewrites outgoing tool-call/tool-result message parts back into the same XML
 * text convention. The SDK core sees a fully native protocol on both sides — schema validation,
 * execution, result feedback, and the multi-step loop all work — while the wire only ever
 * carries user/assistant text. The model reads its own past calls in exactly the format it
 * emits, and results arrive as `<function_results>` user text, a convention Claude models
 * already know (observed hallucinating it unprompted).
 */
export const textToolProtocolMiddleware: LanguageModelMiddleware = {
    specificationVersion: 'v4',

    transformParams: async ({ params }) => ({
        ...params,
        prompt: params.prompt.flatMap<LanguageModelV4Message>((message) => {
            if (message.role === 'tool') {
                const results = message.content
                    .filter((part): part is LanguageModelV4ToolResultPart => part.type === 'tool-result')
                    .map((part) => serializeToolResult(part));

                return results.length
                    ? [{ role: 'user', content: [{ type: 'text', text: formatFunctionResultsBlock(results) }] }]
                    : [];
            }

            if (message.role === 'assistant' && message.content.some((part) => part.type === 'tool-call')) {
                const toolCalls = message.content
                    .filter((part) => part.type === 'tool-call')
                    .map((part) => ({ toolName: part.toolName, input: part.input }));
                const otherParts = message.content.filter(
                    (part) => part.type !== 'tool-call' && part.type !== 'tool-result'
                );

                return [
                    {
                        ...message,
                        content: [...otherParts, { type: 'text', text: formatFunctionCallsBlock(toolCalls) }],
                    },
                ];
            }

            return [message];
        }),
    }),

    wrapGenerate: async ({ doGenerate, params }) => {
        const result = await doGenerate();
        // The names this request actually offered — the whole allow-list for the last-resort
        // recognizer in `extractLeakedToolCalls`. Taken from the outgoing params rather than plumbed
        // in, so it cannot fall out of step with what the model was told it could call.
        const knownTools = (params.tools ?? []).map((tool) => tool.name);
        // Tracked separately: an extraction can yield zero calls (response truncated mid-tag) —
        // the cleaned text must still replace the dangling fragment even with nothing to run.
        let leakFound = false;
        let convertedCallCount = 0;

        const content = result.content.flatMap<(typeof result.content)[number]>((part) => {
            if (part.type !== 'text') {
                return [part];
            }

            const extraction = extractLeakedToolCalls(part.text, knownTools);

            if (!extraction) {
                return [part];
            }

            leakFound = true;
            convertedCallCount += extraction.toolCalls.length;
            console.warn('AI chat: converted leaked <function_calls> XML into native tool calls', extraction);

            return [
                ...(extraction.text ? [{ ...part, text: extraction.text }] : []),
                ...extraction.toolCalls.map((call) => ({
                    type: 'tool-call' as const,
                    toolCallId: generateId(),
                    toolName: call.toolName,
                    input: call.input,
                })),
            ];
        });

        if (!leakFound) {
            return result;
        }

        return {
            ...result,
            content,
            finishReason: convertedCallCount
                ? { ...result.finishReason, unified: 'tool-calls' as const }
                : result.finishReason,
        };
    },
};

function serializeToolResult(part: LanguageModelV4ToolResultPart): {
    toolName: string;
    value: string;
    isError: boolean;
} {
    const output = part.output;

    switch (output.type) {
        case 'text':
        case 'error-text':
            return { toolName: part.toolName, value: output.value, isError: output.type === 'error-text' };
        case 'json':
        case 'error-json':
            return {
                toolName: part.toolName,
                value: JSON.stringify(output.value),
                isError: output.type === 'error-json',
            };
        case 'execution-denied':
            return { toolName: part.toolName, value: output.reason ?? 'execution denied', isError: true };
        default:
            return { toolName: part.toolName, value: JSON.stringify(output), isError: false };
    }
}
